/* ============================================================================
   Droplet PDP — runtime for the dedicated product template (product.droplet-pdp)
   ----------------------------------------------------------------------------
   Works alongside assets/droplet-landing.js, which this template also loads:
   that file already runs the purchase card (gallery, accordions, add to cart
   through Dawn's <product-form>), the Junip rating sync and the site nav. This
   file adds only what the PDP chapters need:

     • one passive scroll/rAF engine that writes each track's progress to --p
     • entrance reveals, lazy video, a scroll-scrubbed install film
     • per-chapter behaviour (steps, app screens, comparison table, stories…)
     • the sticky commerce bar, nav tone over dark chapters, price mirroring

   No libraries, no scroll hijacking. Everything a chapter creates is recorded
   and torn down on shopify:section:unload, and re-created on section:load, so
   the theme editor never stacks listeners.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__dropletPDP) return;

  var RM = window.matchMedia('(prefers-reduced-motion: reduce)');
  var MQ_M = window.matchMedia('(max-width: 768px)');
  /* Phones held sideways are too short for pinned chapters: they get the same
     finished, in-flow composition as reduced motion (see droplet-pdp.css). */
  var SHORT = window.matchMedia('(orientation: landscape) and (max-height: 500px)');
  var STATIC = { get matches() { return RM.matches || SHORT.matches; } };
  var clamp = function (v, a, b) { return Math.min(b, Math.max(a, v)); };
  var qa = function (root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); };

  /* ------------------------------------------------------------ scroll engine */
  var tracks = new Set();
  var active = new Set();
  var frameHooks = new Set();
  var ticking = false;

  var trackIO = ('IntersectionObserver' in window) ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) { if (e.isIntersecting) active.add(e.target); else active.delete(e.target); });
    schedule();
  }, { rootMargin: '40% 0px 40% 0px' }) : null;

  function progressOf(el, vh) {
    var r = el.getBoundingClientRect();
    if (el.getAttribute('data-pdp-track') === 'pass') return clamp((vh - r.top) / (vh + r.height), 0, 1);
    /* Phones: the chapter's words (data-pdp-lead) scroll by normally and only
       the visual (data-pdp-pin) holds. Progress runs from the moment the pin
       locks under the nav to the moment it releases, so no animation is spent
       while the words are still on screen. */
    var pin = el.__pdpPinEl;
    if (pin && MQ_M.matches) {
      var lead = el.__pdpLeadEl;
      var cs = window.getComputedStyle(pin);
      if (cs.position === 'sticky') {
        var stickTop = parseFloat(cs.top) || 0;
        var natural = (lead ? lead.getBoundingClientRect().bottom - r.top : 0) + (parseFloat(cs.marginTop) || 0);
        var start = natural - stickTop;
        var end = r.height - pin.offsetHeight - stickTop;
        if (end - start > 1) return clamp((-r.top - start) / (end - start), 0, 1);
      }
    }
    var range = r.height - vh;
    if (range <= 0) return r.top < 0 ? 1 : 0;
    return clamp(-r.top / range, 0, 1);
  }

  function frame() {
    ticking = false;
    var vh = window.innerHeight;
    /* every track is measured on every frame (there are only a handful):
       WebKit can drop intersection records after a long jump or a momentum
       fling, and a track that missed its record would freeze */
    tracks.forEach(function (el) {
      var raw = STATIC.matches ? 1 : progressOf(el, vh);
      /* a chapter can reserve the start of its track for an intro move of its
         own (phones: One sensor lifts the house into place first). It sees the
         raw progress as --pr; everything else keeps reading --p. */
      var p = el.__pdpMap ? el.__pdpMap(raw) : raw;
      if (el.__pdpMap && el.__pdpR !== raw) { el.__pdpR = raw; el.style.setProperty('--pr', raw.toFixed(4)); }
      if (el.__pdpP === p) return;
      el.__pdpP = p;
      el.style.setProperty('--p', p.toFixed(4));
      if (el.__pdpOnP) el.__pdpOnP(p);
    });
    frameHooks.forEach(function (fn) { try { fn(); } catch (e) {} });
  }
  function schedule() { if (!ticking) { ticking = true; window.requestAnimationFrame(frame); } }
  window.addEventListener('scroll', schedule, { passive: true });
  /* scroll watchdog: iOS can deliver scroll events late or not at all during
     a momentum fling or a programmatic jump. After any touch, wheel, key or
     scroll, watch the scroll position every frame until it has been still
     for about a second, and re-measure whenever it moves. */
  var wdY = -1, wdStill = 0, wdId = 0;
  function watchdog() {
    wdId = 0;
    var y = window.pageYOffset;
    if (y !== wdY) { wdY = y; wdStill = 0; schedule(); } else wdStill++;
    if (wdStill < 60) wdId = window.requestAnimationFrame(watchdog);
  }
  function kick() { wdStill = 0; if (!wdId) wdId = window.requestAnimationFrame(watchdog); }
  ['touchstart', 'touchmove', 'touchend', 'wheel', 'keydown', 'scroll'].forEach(function (ev) { window.addEventListener(ev, kick, { passive: true }); });
  window.addEventListener('resize', schedule, { passive: true });

  function addTrack(el, onP, clean) {
    el.__pdpOnP = onP || null;
    el.__pdpPinEl = el.querySelector('[data-pdp-pin]');
    el.__pdpLeadEl = el.querySelector('[data-pdp-lead]');
    tracks.add(el);
    if (trackIO) trackIO.observe(el); else active.add(el);
    clean.push(function () { tracks.delete(el); active.delete(el); if (trackIO) trackIO.unobserve(el); el.__pdpOnP = null; el.__pdpP = undefined; });
    schedule();
  }
  function addHook(fn, clean) { frameHooks.add(fn); clean.push(function () { frameHooks.delete(fn); }); schedule(); }

  /* ------------------------------------------------------------ helpers */
  function on(clean, t, type, fn, opts) { if (!t) return; t.addEventListener(type, fn, opts); clean.push(function () { t.removeEventListener(type, fn, opts); }); }
  function observe(clean, cb, opts, els) {
    if (!('IntersectionObserver' in window)) { els.forEach(function (el) { cb({ target: el, isIntersecting: true, intersectionRatio: 1 }); }); return null; }
    var io = new IntersectionObserver(function (es) { es.forEach(cb); }, opts);
    els.forEach(function (el) { io.observe(el); });
    clean.push(function () { io.disconnect(); });
    return io;
  }
  function timer(clean, fn, ms) { var id = window.setInterval(fn, ms); clean.push(function () { window.clearInterval(id); }); return id; }
  function playSafe(v) { try { var p = v.play(); if (p && p.catch) p.catch(function () {}); } catch (e) {} }
  function srcFor(v) { return (MQ_M.matches && v.getAttribute('data-src-m')) ? v.getAttribute('data-src-m') : (v.getAttribute('data-src-d') || v.getAttribute('data-src')); }
  function hydrate(v) {
    if (v.__pdpSrc) return;
    var s = srcFor(v);
    if (!s) return;
    v.__pdpSrc = s; v.src = s;
    try { v.load(); } catch (e) {}
  }

  /* add to cart through the landing's Shopify cart bridge (Dawn <product-form>) */
  function available() { var b = document.getElementById('buybox'); return !b || b.getAttribute('data-available') !== 'false'; }
  function bindAdd(btn, statusEl, clean) {
    if (!btn) return;
    var busy = false;
    if (!available()) { btn.disabled = true; }
    on(clean, btn, 'click', function () {
      if (busy || btn.disabled) return;
      if (!window.__dropletCartAdd) { var c = document.getElementById('bbCta'); if (c) c.click(); return; }
      busy = true;
      var label = btn.textContent;
      btn.setAttribute('data-state', 'loading'); btn.textContent = 'Adding…';
      if (statusEl) statusEl.textContent = 'Adding Droplet to your cart…';
      window.__dropletCartAdd().then(function () {
        btn.setAttribute('data-state', 'success'); btn.textContent = 'Added ✓';
        if (statusEl) statusEl.textContent = 'Droplet added to your cart.';
        window.setTimeout(function () { btn.removeAttribute('data-state'); btn.textContent = label; busy = false; }, 2200);
      }).catch(function () {
        btn.removeAttribute('data-state'); btn.textContent = label; busy = false;
        if (statusEl) statusEl.textContent = 'Something went wrong, please try again.';
      });
    });
  }

  /* one price everywhere: mirror the purchase card, the page's source of truth */
  function mirrorPrices(root) {
    var now = document.querySelector('#buybox .bb__price-now');
    if (!now) return;
    var was = document.querySelector('#buybox .bb__price-was');
    var save = document.querySelector('#buybox .bb__save');
    qa(root, '[data-pdp-price]').forEach(function (el) {
      var k = el.getAttribute('data-pdp-price');
      var src = k === 'now' ? now : (k === 'was' ? was : save);
      if (src && src.textContent.trim()) { el.textContent = src.textContent.trim(); el.hidden = false; }
      else if (k !== 'now') { el.textContent = ''; el.hidden = true; }
    });
  }

  function smoothTo(el) {
    if (!el) return;
    var nav = document.getElementById('nav');
    var y = el.getBoundingClientRect().top + window.pageYOffset - ((nav && nav.offsetHeight) || 0) + 1;
    window.scrollTo({ top: Math.max(0, y), behavior: RM.matches ? 'auto' : 'smooth' });
  }

  /* ------------------------------------------------------------ generic per section */
  /* Gradient phrases never break across lines: a wrapped phrase restarts
     the gradient on every line. They are kept on one line (white-space:
     nowrap in the CSS); when the phrase is wider than its headline's measure,
     the headline is first allowed to widen up to its container, and only if
     the phrase is still too wide is the headline's type scaled down to fit.
     Re-run on resize and once web fonts have loaded. */
  var GRAD_SEL = '.gradient-text, .pdp-prec__accent';
  function fitGradients(scope) {
    qa(scope, GRAD_SEL).forEach(function (span) {
      var host = span.closest('h1, h2, h3, p');
      if (!host || !span.closest('.droplet-pdp')) return;
      host.style.maxWidth = ''; host.style.fontSize = '';
      var need = span.getBoundingClientRect().width;
      var cs = getComputedStyle(host);
      var pad = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
      var box = host.clientWidth - pad;
      var par = host.parentElement, pcs = getComputedStyle(par);
      var room = par.clientWidth - (parseFloat(pcs.paddingLeft) || 0) - (parseFloat(pcs.paddingRight) || 0) - pad;
      if (need > room + 0.5) { host.style.fontSize = (parseFloat(cs.fontSize) * (room / need) * 0.985).toFixed(2) + 'px'; return; }
      if (need > box + 0.5) host.style.maxWidth = Math.ceil(need + pad + 2) + 'px';
    });
  }
  var fitT = 0;
  window.addEventListener('resize', function () { window.clearTimeout(fitT); fitT = window.setTimeout(function () { fitGradients(document); }, 80); }, { passive: true });
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { fitGradients(document); });

  function generic(sec, clean) {
    mirrorPrices(sec);
    fitGradients(sec);

    var reveals = qa(sec, '[data-pdp-reveal]');
    if (RM.matches) reveals.forEach(function (el) { el.classList.add('is-in'); });
    else observe(clean, function (e) {
      if (e.isIntersecting) { e.target.classList.add('is-in'); }
    }, { threshold: 0.14, rootMargin: '0px 0px -6% 0px' }, reveals);

    /* lazy, in-view-only playback for ambient films */
    var lazy = qa(sec, 'video[data-pdp-lazy]');
    if (lazy.length) {
      observe(clean, function (e) {
        var v = e.target;
        if (e.isIntersecting) {
          if (RM.matches && !v.hasAttribute('data-pdp-scrub')) return;
          hydrate(v);
          if (v.hasAttribute('data-pdp-autoplay') && !RM.matches) playSafe(v);
        } else if (v.hasAttribute('data-pdp-autoplay')) { try { v.pause(); } catch (err) {} }
      }, { rootMargin: '300px 0px 300px 0px' }, lazy);
      lazy.forEach(function (v) {
        on(clean, v, 'playing', function () { v.classList.add('is-playing'); });
      });
    }

    /* click-to-play customer videos */
    qa(sec, '[data-pdp-video]').forEach(function (wrap) { videoCard(wrap, clean); });
  }

  function videoCard(wrap, clean) {
    var v = wrap.querySelector('video');
    var play = wrap.querySelector('[data-pdp-play]');
    var sound = wrap.querySelector('[data-pdp-sound]');
    if (!v || !play) return;
    var muted = v.hasAttribute('muted');
    function setSound(onn) {
      v.muted = !onn;
      if (sound) { sound.setAttribute('aria-pressed', onn ? 'true' : 'false'); var l = sound.querySelector('[data-sound-label]'); if (l) l.textContent = onn ? 'Sound off' : 'Sound on'; }
    }
    on(clean, play, 'click', function () {
      if (!v.getAttribute('src')) { v.src = v.getAttribute('data-src'); }
      v.muted = muted; /* customer videos in the stories start muted; the single proof video plays with sound */
      v.controls = false;
      playSafe(v);
      wrap.classList.add('is-playing'); wrap.classList.remove('is-paused');
      if (sound) sound.hidden = false;
    });
    on(clean, v, 'click', function () {
      if (v.paused) { playSafe(v); wrap.classList.remove('is-paused'); }
      else { v.pause(); wrap.classList.add('is-paused'); }
    });
    on(clean, v, 'ended', function () { wrap.classList.remove('is-playing'); });
    if (sound) on(clean, sound, 'click', function () { setSound(v.muted); });
    /* never keep playing (or talking) off-screen */
    observe(clean, function (e) { if (!e.isIntersecting && !v.paused) { v.pause(); wrap.classList.add('is-paused'); } }, { threshold: 0 }, [wrap]);
    wrap.__pdpLoad = function (src, poster, label, quote) {
      try { v.pause(); } catch (e) {}
      v.removeAttribute('src'); v.setAttribute('data-src', src);
      if (label) v.setAttribute('aria-label', label);
      var img = wrap.querySelector('.pdp-video__poster'); if (img && poster) img.src = poster;
      var q = wrap.querySelector('[data-now-quote]'); if (q && quote) q.textContent = '“' + quote + '”';
      wrap.classList.remove('is-playing', 'is-paused');
      if (sound) { sound.hidden = true; setSound(false); }
      try { v.load(); } catch (e) {}
    };
  }

  /* ------------------------------------------------------------ chapters */
  var S = {};

  /* Chapter 1 — nav tone, nav CTA, sticky bar */
  S.buy = function (sec, clean) {
    var nav = document.getElementById('nav');
    var bar = sec.querySelector('#pdpStickyBar');
    var buybox = document.getElementById('buybox');

    /* the nav's Order button scrolls to the card on this page */
    if (nav) qa(document, '.nav__cta, .nav-overlay__cta').forEach(function (a) {
      var href = a.getAttribute('href') || '';
      if (/#buybox$/.test(href)) {
        a.setAttribute('href', '#buybox'); a.setAttribute('data-pdp-cta', 'nav');
        on(clean, a, 'click', function (e) { if (buybox) { e.preventDefault(); smoothTo(buybox); } });
      }
    });

    /* nav tone follows whatever chapter is under it */
    if (nav) {
      var navDark = false;
      addHook(function () {
        var y = Math.round(nav.offsetHeight * 0.5);
        var x = Math.round(window.innerWidth / 2);
        var stack = document.elementsFromPoint ? document.elementsFromPoint(x, y) : [];
        var tone = 'light';
        for (var i = 0; i < stack.length; i++) {
          var el = stack[i];
          if (nav.contains(el) || el === nav) continue;
          if (bar && bar.contains(el)) continue;
          if (el.closest && el.closest('.shopify-section-group-footer-group, footer')) { tone = 'dark'; break; }
          var t = el.closest && el.closest('[data-pdp-tone]');
          if (t) { tone = t.getAttribute('data-pdp-tone'); break; }
          if (el.closest && el.closest('.droplet-pdp')) break;
        }
        var d = tone === 'dark';
        if (d !== navDark) { navDark = d; nav.classList.toggle('is-dark', d); }
      }, clean);
      clean.push(function () { nav.classList.remove('is-dark'); });
    }

    if (!bar) return;
    bindAdd(bar.querySelector('[data-pdp-add]'), bar.querySelector('[data-pdp-add-status]'), clean);

    var state = { buybox: true, past: false, final: false, footer: false, drawer: false, menu: false };
    function render() {
      var show = !state.buybox && state.past && !state.final && !state.footer && !state.drawer && !state.menu;
      bar.classList.toggle('is-shown', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      qa(bar, 'a, button').forEach(function (el) { if (show) el.removeAttribute('tabindex'); else el.setAttribute('tabindex', '-1'); });
      liftChat(show);
    }
    /* on phones the store's chat launcher (Intercom app embed) sits on the
       bar's Add to Cart; lift it above the bar while the bar is up, through
       Intercom's own vertical_padding setting. Desktop is left as it was. */
    var chatLift = null;
    function liftChat(show) {
      var want = show && MQ_M.matches ? Math.round(bar.offsetHeight || 80) + 16 : null;
      document.documentElement.classList.toggle('pdp-bar-up', !!want);
      if (want === chatLift) return;
      chatLift = want;
      try {
        window.intercomSettings = window.intercomSettings || {};
        if (want) window.intercomSettings.vertical_padding = want; else delete window.intercomSettings.vertical_padding;
        if (typeof window.Intercom === 'function') window.Intercom('update', { vertical_padding: want || 20 });
      } catch (e) {}
    }
    clean.push(function () { liftChat(false); document.documentElement.classList.remove('pdp-bar-up'); });
    render();
    if (buybox) observe(clean, function (e) {
      state.buybox = e.isIntersecting;
      state.past = !e.isIntersecting && e.boundingClientRect.top < 0;
      render();
    }, { threshold: 0, rootMargin: '0px 0px -30% 0px' }, [buybox.querySelector('.bb__buy') || buybox]);
    /* belt and braces: a long jump (an in-page link, a restored scroll position)
       can leave WebKit without a fresh intersection record, so the purchase
       card's position is also read directly on scroll */
    if (buybox) {
      var bbEl = buybox.querySelector('.bb__buy') || buybox, bbRaf = 0;
      var bbRead = function () {
        bbRaf = 0;
        var r = bbEl.getBoundingClientRect(), vh = window.innerHeight;
        var inView = r.bottom > 0 && r.top < vh * 0.7;
        var past = !inView && r.top < 0;
        if (inView !== state.buybox || past !== state.past) { state.buybox = inView; state.past = past; render(); }
      };
      on(clean, window, 'scroll', function () { if (!bbRaf) bbRaf = window.requestAnimationFrame(bbRead); }, { passive: true });
    }
    var fin = document.querySelector('[data-pdp-final]');
    if (fin) observe(clean, function (e) { state.final = e.isIntersecting; render(); }, { threshold: 0.2 }, [fin]);
    var foot = document.querySelector('.shopify-section-group-footer-group, footer');
    if (foot) observe(clean, function (e) { state.footer = e.isIntersecting; render(); }, { threshold: 0 }, [foot]);

    /* cart drawer / notification / mobile menu take focus: get out of the way */
    var drawer = document.querySelector('cart-drawer');
    var html = document.documentElement;
    function checkOverlays() {
      state.drawer = !!(drawer && (drawer.classList.contains('active') || drawer.classList.contains('animate'))) || document.body.classList.contains('overflow-hidden');
      state.menu = html.classList.contains('droplet-nav-locked');
      render();
    }
    if ('MutationObserver' in window) {
      var mo = new MutationObserver(checkOverlays);
      if (drawer) mo.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      mo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      mo.observe(html, { attributes: true, attributeFilter: ['class'] });
      clean.push(function () { mo.disconnect(); });
    }
  };

  S.problem = function (sec, clean) {
    var track = sec.querySelector('[data-pdp-track]');
    if (track) addTrack(track, null, clean);
  };

  /* One sensor: draw by scroll, then run water events through the branches */
  S.mainline = function (sec, clean) {
    var track = sec.querySelector('[data-pdp-track]');
    var fig = sec.querySelector('.pdp-main__visual');
    var nameEl = sec.querySelector('[data-event-name]');
    if (!track || !fig) return;
    var order = ['shower', 'toilet', 'faucet', 'laundry', 'irrigation'];
    var labels = {};
    qa(sec, '.pdp-main__pin').forEach(function (p) { var b = p.querySelector('b'); labels[p.getAttribute('data-pin')] = b ? b.textContent : p.getAttribute('data-pin'); });
    var i = -1, loop = null;
    function step() {
      i = (i + 1) % order.length;
      fig.setAttribute('data-event', order[i]);
      if (nameEl) nameEl.textContent = labels[order[i]];
    }
    function start() { if (loop || STATIC.matches) return; step(); loop = window.setInterval(step, 2400); }
    function stop() { if (!loop) return; window.clearInterval(loop); loop = null; fig.setAttribute('data-event', ''); }
    clean.push(stop);
    /* phones: headline and lede hold at the top; the first stretch of scroll
       fades the three proof lines out and lifts the house up under the lede,
       then the plumbing draws exactly as on desktop over the rest */
    if (MQ_M.matches && !STATIC.matches) {
      var proofs = sec.querySelector('.pdp-main__proofs');
      var lift = function () { if (proofs) track.style.setProperty('--lift', (proofs.offsetHeight + 14) + 'px'); };
      lift();
      on(clean, window, 'resize', lift);
      if (window.ResizeObserver && proofs) { var ro = new ResizeObserver(lift); ro.observe(proofs); clean.push(function () { ro.disconnect(); }); }
      track.__pdpMap = function (r) { return clamp((r - 0.2) / 0.8, 0, 1); };
      clean.push(function () { track.__pdpMap = null; track.__pdpR = undefined; track.style.removeProperty('--pr'); track.style.removeProperty('--lift'); });
    }
    var lastP = 0;
    addTrack(track, function (p) { lastP = p; if (p >= 0.66 && isNear(track)) start(); else stop(); }, clean);
    /* once the chapter has scrolled away, --p stops changing: stop the loop there too */
    observe(clean, function (e) { if (!e.isIntersecting) stop(); else if (lastP >= 0.66) start(); }, { threshold: 0 }, [track]);
  };
  function isNear(el) { var r = el.getBoundingClientRect(); return r.bottom > 0 && r.top < window.innerHeight; }

  /* ------------------------------------------------------------ frame-by-frame film scrubbing
     Same approach as the home page's app demo (droplet-landing.js, module 4):
     one seek in flight at a time with the latest target winning, never seeking
     past what has buffered, a one-shot play/pause to wake Safari's decoder, a
     watchdog for a lost 'seeked', and an eased follow so the film glides with
     the scroll instead of stepping. The films are all-intra, so fastSeek lands
     on the exact frame. */
  function makeScrubber(v, clean) {
    var dur = 0, ready = false, full = false, busy = false, pend = null, last = -1, start = 0, primed = false, loaded = false;
    function vinit() {
      if (ready || !v.duration || !isFinite(v.duration) || v.duration <= 0) return;
      dur = v.duration; ready = true; v.classList.add('is-ready');
      if (pend != null) { var t = pend; pend = null; seek(t); }
    }
    function prime() {
      if (primed || !v.getAttribute('src')) return; primed = true;
      try { var pr = v.play(); if (pr && pr.then) pr.then(function () { v.pause(); }).catch(function () {}); else v.pause(); } catch (e) {}
    }
    function bend() { try { return v.buffered.length ? v.buffered.end(v.buffered.length - 1) : 0; } catch (e) { return dur; } }
    function seek(t) {
      if (!ready) { pend = t; return; }
      t = clamp(t, 0, dur - 0.05);
      if (!full) { var safe = bend() - 0.1; if (safe > 0 && t > safe) t = safe; }
      if (busy) { pend = t; return; }
      if (Math.abs(t - last) < 0.012) return;
      last = t; busy = true; start = performance.now();
      if (v.fastSeek) { try { v.fastSeek(t); } catch (e) { v.currentTime = t; } } else v.currentTime = t;
    }
    ['loadedmetadata', 'durationchange', 'canplay'].forEach(function (ev) { on(clean, v, ev, vinit); });
    on(clean, v, 'progress', function () { try { if (v.buffered.length && v.buffered.end(v.buffered.length - 1) >= (v.duration || 1) - 0.1) full = true; } catch (e) {} });
    on(clean, v, 'seeked', function () { busy = false; if (pend != null) { var t = pend; pend = null; seek(t); } });
    on(clean, v, 'loadeddata', prime);
    return {
      seek: seek, prime: prime,
      tick: function () { if (busy && performance.now() - start > 250) busy = false; },
      wake: function () { busy = false; last = -1; primed = false; prime(); },
      load: function () {
        if (loaded) return; loaded = true;
        var src = srcFor(v); if (!src) return;
        v.src = src; try { v.load(); } catch (e) {}
      },
      get dur() { return dur; }, get ready() { return ready; }
    };
  }
  /* rAF loop that runs only while the section is near the viewport: eases the
     scroll progress and hands it to apply(p). */
  function scrubLoop(sec, track, clean, apply, onNear) {
    var near = false, cur = -1, id = 0, woke = false;
    function loop() {
      id = 0;
      if (!near) return;
      var target = track.__pdpP == null ? 0 : track.__pdpP;
      cur = cur < 0 ? target : cur + (target - cur) * 0.14;
      if (Math.abs(target - cur) < 0.0004) cur = target;
      apply(cur, woke); woke = false;
      id = window.requestAnimationFrame(loop);
    }
    var setNear = function (n) {
      if (n === near) return;
      near = n;
      if (near) { if (onNear) onNear(); if (!id) id = window.requestAnimationFrame(loop); }
    };
    observe(clean, function (e) { setNear(e.isIntersecting); }, { rootMargin: '120% 0px 120% 0px' }, [track]);
    /* same safety net as the tracks: also decide "near" from the live position */
    addHook(function () {
      var r = track.getBoundingClientRect(), vh = window.innerHeight;
      setNear(r.bottom > -1.2 * vh && r.top < 2.2 * vh);
    }, clean);
    var wake = function () { if (!document.hidden) woke = true; };
    on(clean, document, 'visibilitychange', wake);
    on(clean, window, 'pageshow', wake);
    clean.push(function () { near = false; if (id) window.cancelAnimationFrame(id); });
  }
  function primeOnGesture(clean, scrubbers) {
    var fn = function () { scrubbers.forEach(function (sc) { sc.prime(); }); };
    ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach(function (ev) { on(clean, window, ev, fn, { passive: true }); });
  }
  var sm = function (t) { t = clamp(t, 0, 1); return t * t * (3 - 2 * t); };
  /* p within [a,b] -> film time within [t0,t1], with a short hold at each end */
  function seg(p, a, b, t0, t1) { var u = clamp((p - a) / (b - a), 0, 1); return t0 + sm((u - 0.06) / 0.82) * (t1 - t0); }

  S.moment = function (sec, clean) {
    var track = sec.querySelector('[data-pdp-track]');
    if (!track) return;
    var cuts = [0.34, 0.48, 0.62, 0.78];
    addTrack(track, function (p) {
      var s = 0; for (var k = 0; k < cuts.length; k++) if (p >= cuts[k]) s = k + 1;
      if (STATIC.matches) s = 4;
      if (track.getAttribute('data-step') !== String(s)) track.setAttribute('data-step', s);
    }, clean);

    /* the phone wakes on the real alert film: the notification lands during
       "Your phone knows", then the alert opens during "So you can act" */
    /* the leak drop falls from the chart's "flow that shouldn't be there"
       onto the line above the story text: measure that fall */
    var drop = sec.querySelector('.pdp-moment__drop'), chartEl = sec.querySelector('.pdp-moment__chart');
    if (drop && chartEl) {
      var fall = function () {
        var li = drop.parentNode;
        var d = li.getBoundingClientRect().top - chartEl.getBoundingClientRect().top - chartEl.offsetHeight * 0.45;
        track.style.setProperty('--fall', Math.max(60, Math.round(d)) + 'px');
      };
      fall();
      on(clean, window, 'resize', fall);
      clean.push(function () { track.style.removeProperty('--fall'); });
    }

    var v = sec.querySelector('.pdp-moment__vid');
    var phone = sec.querySelector('.pdp-moment__phone');
    if (!v || STATIC.matches) return;
    var sc = makeScrubber(v, clean);
    on(clean, v, 'loadedmetadata', function () { if (phone) phone.classList.add('has-video'); });
    clean.push(function () { if (phone) phone.classList.remove('has-video'); });
    primeOnGesture(clean, [sc]);
    scrubLoop(sec, track, clean, function (p, woke) {
      if (woke) sc.wake();
      sc.tick();
      var t = p < 0.78 ? seg(p, 0.62, 0.76, 0, 1.45) : seg(p, 0.78, 0.93, 1.65, 2.55);
      sc.seek(t);
    }, function () { sc.load(); });
  };

  S.app = function (sec, clean) {
    var track = sec.querySelector('[data-pdp-track]');
    var states = qa(sec, '.pdp-app__state');
    var row = sec.querySelector('[data-pdp-app-track]');
    if (!track || !states.length) return;
    var N = states.length;
    var cur = -1;
    function set(i) {
      if (i === cur) return;
      cur = i;
      track.setAttribute('data-state', i);
      states.forEach(function (st, k) { st.classList.toggle('is-active', k === i); });
    }
    set(0);

    var btns = qa(sec, '[data-range]');
    var imgs = qa(sec, '[data-range-img]');
    function range(key) {
      btns.forEach(function (b) { var onn = b.getAttribute('data-range') === key; b.classList.toggle('is-on', onn); b.setAttribute('aria-pressed', onn ? 'true' : 'false'); });
      imgs.forEach(function (im) { im.classList.toggle('is-on', im.getAttribute('data-range-img') === key); });
    }

    var device = sec.querySelector('.pdp-app__device');
    var vA = sec.querySelector('.pdp-app__vid[data-vid="alert"]');
    var vB = sec.querySelector('.pdp-app__vid[data-vid="usage"]');
    var video = !STATIC.matches && device && vA && vB;

    if (video) {
      /* one phone, two films. Chapters 1-3 scrub the alert film (lock-screen
         notification -> alert details -> the live 10-minute chart); chapters 4-5
         scrub the dashboard film (2026-10 take, 20.9s: today -> the day ->
         the live hour -> this year -> July -> what's using water). Chapter 4
         carries most of that film, so it gets a longer share of the scroll. */
      track.classList.add('is-video');
      clean.push(function () { track.classList.remove('is-video'); });
      addTrack(track, null, clean);
      var poster = device.querySelector('.pdp-app__devposter');
      var posterA = poster ? poster.getAttribute('src') : '';
      var stillYear = (sec.querySelector('[data-range-img="day"]') || {}).src || posterA;
      var scA = makeScrubber(vA, clean), scB = makeScrubber(vB, clean);
      primeOnGesture(clean, [scA, scB]);
      var A_SEG = [[0, 1.45], [1.65, 2.55], [2.6, 4.1]];
      var B_SEG = [[0, 16.6], [16.6, 20.85]];
      /* chapter 4, u -> film time: a beat on today, the day scrolling back,
         the live hour, then the year and the month */
      var B4 = [[0, 0], [0.1, 1.6], [0.26, 4.4], [0.62, 12.6], [0.8, 14.6], [1, 16.6]];
      var b4 = function (u) {
        for (var k = 1; k < B4.length; k++) if (u <= B4[k][0]) return B4[k - 1][1] + (u - B4[k - 1][0]) / (B4[k][0] - B4[k - 1][0]) * (B4[k][1] - B4[k - 1][1]);
        return B4[B4.length - 1][1];
      };
      var RANGE_AT = [['day', 0], ['hour', 4.4], ['year', 12.6], ['month', 14.6]];
      var CW = [1, 1, 1, 1.9, 1.1], CW_T = 0, CW_A = [];
      CW.forEach(function (w) { CW_A.push(CW_T); CW_T += w; });
      var locate = function (p) {
        var x = clamp(p, 0, 0.99999) * CW_T, k = 0;
        while (k < CW.length - 1 && x >= CW_A[k + 1]) k++;
        return [k, (x - CW_A[k]) / CW[k]];
      };
      var lastKey = '';
      var onUsage = null;
      scrubLoop(sec, track, clean, function (p, woke) {
        if (woke) { scA.wake(); scB.wake(); }
        scA.tick(); scB.tick();
        var lu = locate(p), i = lu[0], u = lu[1];
        set(i);
        var usage = i >= 3;
        if (i >= 1) scB.load(); /* fetch the usage film while the alert chapters play */
        if (usage !== onUsage) {
          onUsage = usage;
          vA.classList.toggle('is-on', !usage); vB.classList.toggle('is-on', usage);
          if (poster) poster.src = usage ? stillYear : posterA;
          if (usage) scB.load();
        }
        if (!usage) {
          var sa = A_SEG[i]; scA.seek(seg(u, 0, 1, sa[0], sa[1]));
        } else {
          var sb = B_SEG[i - 3], tb = i === 3 ? b4(clamp(u, 0, 1)) : seg(u, 0, 1, sb[0], sb[1]); scB.seek(tb);
          if (i === 3) {
            var key = 'day'; RANGE_AT.forEach(function (r) { if (tb >= r[1]) key = r[0]; });
            if (key !== lastKey) { lastKey = key; range(key); }
          }
        }
      }, function () { scA.load(); if (cur >= 2) scB.load(); });

      /* tapping a range pill scrolls to the point in the film where it shows */
      btns.forEach(function (b) {
        on(clean, b, 'click', function () {
          var tt = 0; RANGE_AT.forEach(function (r) { if (r[0] === b.getAttribute('data-range')) tt = r[1] + 0.35; });
          var lo = 0, hi = 1;
          for (var k = 0; k < 24; k++) { var mid = (lo + hi) / 2; if (b4(mid) < tt) lo = mid; else hi = mid; }
          var pTarget = (CW_A[3] + lo * CW[3]) / CW_T;
          var r = track.getBoundingClientRect();
          var y = r.top + window.pageYOffset + pTarget * (track.offsetHeight - window.innerHeight);
          window.scrollTo({ top: y, behavior: 'smooth' });
        });
      });
      return;
    }

    /* reduced motion / fallback: the still screens */
    addTrack(track, function (p) {
      if (MQ_M.matches || STATIC.matches) return;
      set(clamp(Math.floor(p * N * 0.999), 0, N - 1));
    }, clean);
    if (row) on(clean, row, 'scroll', function () {
      if (!MQ_M.matches) return;
      var mid = row.scrollLeft + row.clientWidth / 2, best = 0, bd = 1e9;
      states.forEach(function (st, k) { var c = st.offsetLeft + st.offsetWidth / 2; var d = Math.abs(c - mid); if (d < bd) { bd = d; best = k; } });
      set(best);
    }, { passive: true });
    btns.forEach(function (b) { on(clean, b, 'click', function () { range(b.getAttribute('data-range')); }); });
  };

  S.install = function (sec, clean) {
    var track = sec.querySelector('[data-pdp-track]');
    var v = sec.querySelector('video[data-pdp-scrub]');
    var beats = qa(sec, '.pdp-install__beats li');
    if (!track) return;
    /* The 7s cut in five beats: pipe arrives, universal clamp, clamp closes,
       light turns blue, then "App. Done.". Each beat gets an equal share of the scroll
       (the film's own beats are uneven: "clamp closes" is under a second), so
       every label holds long enough to read. "App. Done." then holds on the
       last frame. */
    /* the film stops on the frame where the light has turned blue and holds
       it to the end (the last second of the cut cracks the housing open,
       which reads as a fault); the setup notification lands on that frame */
    var T = [[0, 1.2], [1.2, 3.7], [3.7, 4.4], [4.4, 5.0]]; /* no separate power beat: blue light, then the app */
    var FILM_END = 0.78, DONE = 0.82;
    function beatFor(p) {
      if (p >= DONE) return 5;
      return clamp(Math.floor(clamp(p / FILM_END, 0, 0.99999) * T.length), 0, T.length - 1);
    }
    function timeFor(p) {
      var x = clamp(p / FILM_END, 0, 1) * T.length, i = Math.min(T.length - 1, Math.floor(x)), u = x - i;
      return T[i][0] + u * (T[i][1] - T[i][0]);
    }
    addTrack(track, function (p) {
      var b = STATIC.matches ? 5 : beatFor(p);
      if (track.getAttribute('data-beat') !== String(b)) {
        track.setAttribute('data-beat', b);
        beats.forEach(function (li) { li.classList.toggle('is-past', +li.getAttribute('data-b') < b); });
      }
    }, clean);
    if (!v || STATIC.matches) return;
    var sc = makeScrubber(v, clean);
    primeOnGesture(clean, [sc]);
    scrubLoop(sec, track, clean, function (p, woke) {
      if (woke) sc.wake();
      sc.tick();
      sc.seek(timeFor(p));
    }, function () { sc.load(); });
  };

  S.tech = function (sec, clean) {
    var track = sec.querySelector('[data-pdp-track]');
    if (!track) return;
    var cuts = [0.12, 0.34, 0.56, 0.78];
    if (MQ_M.matches && !STATIC.matches) {
      /* phones: the first fifth of the track is the entrance (the headline
         lifts away, the pipe settles into place); the four numbers then get an
         equal, longer share of the rest */
      cuts = [0.02, 0.27, 0.52, 0.77];
      track.__pdpMap = function (r) { return clamp((r - 0.2) / 0.8, 0, 1); };
      /* a closer camera on the pipe: the device and both walls fill the width */
      var svg = sec.querySelector('.pdp-tech__svg');
      var vb = svg ? svg.getAttribute('viewBox') : null;
      if (svg) svg.setAttribute('viewBox', '260 -30 480 400');
      /* size the pipe so headline + pipe + one number share the screen, then
         work out how far the pipe block has to travel from "tucked right under
         the headline" to its resting place above the numbers */
      var wrapEl = sec.querySelector('.pdp-tech__wrap'), head = sec.querySelector('.pdp-tech__head'),
          block = sec.querySelector('.pdp-tech__pin'), stats = sec.querySelector('.pdp-tech__stats');
      var measure = function () {
        if (!wrapEl || !head || !block || !stats) return;
        /* the numbers sit at the top, where the headline was; the pipe runs
           edge to edge below them, as large as the room allows, with clear
           air between the two; it starts tucked under the headline and drops
           into that place as the headline lifts away */
        var avail = wrapEl.clientHeight, headBottom = head.offsetTop + head.offsetHeight;
        var statsBottom = 6 + stats.offsetHeight, GAP = 30;
        var vizMax = window.innerWidth / 1.2;
        var room = avail - statsBottom - GAP - 8;
        var vizh = clamp(room, Math.min(160, vizMax), vizMax);
        var top = statsBottom + GAP + Math.max(0, (room - vizh) * 0.35);
        track.style.setProperty('--vizh', Math.round(vizh) + 'px');
        track.style.setProperty('--btop', Math.round(top) + 'px');
        track.style.setProperty('--shift', Math.round(headBottom + 12 - top) + 'px');
      };
      measure();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
      if (window.ResizeObserver) { var tro = new ResizeObserver(measure); tro.observe(head); tro.observe(stats); clean.push(function () { tro.disconnect(); }); }
      on(clean, window, 'resize', measure);
      clean.push(function () {
        if (svg && vb) svg.setAttribute('viewBox', vb);
        track.__pdpMap = null; track.__pdpR = undefined;
        track.style.removeProperty('--pr'); track.style.removeProperty('--vizh'); track.style.removeProperty('--shift'); track.style.removeProperty('--btop');
      });
    }
    addTrack(track, function (p) {
      var s = 0; for (var k = 0; k < cuts.length; k++) if (p >= cuts[k]) s = k + 1;
      if (STATIC.matches) s = 4;
      if (track.getAttribute('data-step') !== String(s)) track.setAttribute('data-step', s);
    }, clean);
  };

  S.precision = function (sec, clean) {
    var track = sec.querySelector('[data-pdp-track]');
    if (!track) return;
    var cuts = [0.2, 0.36, 0.52];
    /* phones: every flow gets the same, longer hold, "a thread" included */
    if (MQ_M.matches && !STATIC.matches) cuts = [0.17, 0.33, 0.49];
    addTrack(track, function (p) {
      var s = 0; for (var k = 0; k < cuts.length; k++) if (p >= cuts[k]) s = k + 1;
      if (STATIC.matches) s = 3;
      if (track.getAttribute('data-step') !== String(s)) track.setAttribute('data-step', s);
    }, clean);
  };

  /* Comparison: a real <table> from the home page's JSON; key rows first */
  S.compare = function (sec, clean) {
    var host = sec.querySelector('[data-pdp-cmp]');
    var root = sec.querySelector('.pdp-cmp');
    if (!host || !root) return;
    var cfg, imgs = {}, keys = [];
    try { cfg = JSON.parse(sec.querySelector('[data-pdp-cmp-config]').textContent); } catch (e) { cfg = null; }
    try { imgs = JSON.parse(sec.querySelector('[data-pdp-cmp-images]').textContent); } catch (e) {}
    try { keys = JSON.parse(sec.querySelector('[data-pdp-cmp-keys]').textContent).map(function (k) { return String(k).trim().toLowerCase(); }); } catch (e) {}
    if (!cfg || !cfg.products || !cfg.groups) { host.textContent = ''; return; }
    var esc = function (s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); };
    var ICON = { yes: '✓', no: '✕', cond: '~' };
    var rows = [];
    cfg.groups.forEach(function (g) { (g.features || []).forEach(function (f) { rows.push(f); }); });
    var keyRows = [], extra = [];
    rows.forEach(function (f) { (keys.indexOf(String(f.label).trim().toLowerCase()) > -1 ? keyRows : extra).push(f); });
    keyRows.sort(function (a, b) { return keys.indexOf(a.label.trim().toLowerCase()) - keys.indexOf(b.label.trim().toLowerCase()); });
    var h = '<table role="table"><caption class="pdp-sr">Droplet compared with meter-mounted monitors and in-line shutoff systems</caption>';
    h += '<colgroup><col class="pdp-cmp__c-label"></colgroup><thead role="rowgroup"><tr role="row"><td role="cell"></td>';
    cfg.products.forEach(function (p) {
      h += '<th role="columnheader" scope="col" data-col="' + esc(p.id) + '"' + (p.featured ? ' class="is-us"' : '') + '><span class="pdp-cmp__prod">' +
        (imgs[p.id] ? '<img src="' + esc(imgs[p.id]) + '" alt="" width="300" height="231" loading="lazy" decoding="async">' : '') +
        '<span>' + esc(p.featured ? p.name : (p.short || p.name)) + '</span></span></th>';
    });
    h += '</tr></thead><tbody role="rowgroup">';
    function row(f, isExtra) {
      var r = '<tr role="row"' + (isExtra ? ' class="is-extra"' : '') + '><th role="rowheader" scope="row">' + esc(f.label) + '</th>';
      cfg.products.forEach(function (p) {
        var v = (f.values || {})[p.id] || {};
        var ic = ICON[v.kind] ? '<span class="pdp-cmp__ic pdp-cmp__ic--' + esc(v.kind) + '" aria-hidden="true">' + ICON[v.kind] + '</span>' : '';
        r += '<td role="cell" data-col="' + esc(p.id) + '"' + (p.featured ? ' class="is-us"' : '') + '><span class="pdp-cmp__cell"><span class="pdp-cmp__big">' + ic + esc(v.big || '') + '</span>' +
          (v.sub ? '<span class="pdp-cmp__sub">' + esc(v.sub) + '</span>' : '') + '</span></td>';
      });
      return r + '</tr>';
    }
    keyRows.forEach(function (f) { h += row(f, false); });
    extra.forEach(function (f) { h += row(f, true); });
    h += '</tbody></table>';
    host.innerHTML = h;

    var toggle = sec.querySelector('[data-pdp-cmp-toggle]');
    if (toggle) {
      if (!extra.length) toggle.hidden = true;
      on(clean, toggle, 'click', function () {
        var open = !root.classList.contains('is-expanded');
        root.classList.toggle('is-expanded', open);
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        toggle.querySelector('[data-closed]').hidden = open;
        toggle.querySelector('[data-open]').hidden = !open;
      });
    }
    qa(sec, '.pdp-cmp__select [data-alt]').forEach(function (b) {
      on(clean, b, 'click', function () {
        var alt = b.getAttribute('data-alt');
        root.setAttribute('data-alt', alt);
        qa(sec, '.pdp-cmp__select [data-alt]').forEach(function (x) { var onn = x === b; x.classList.toggle('is-on', onn); x.setAttribute('aria-pressed', onn ? 'true' : 'false'); });
      });
    });
    bindAdd(sec.querySelector('[data-pdp-add]'), sec.querySelector('[data-pdp-add-status]'), clean);
  };

  S.homes = function (sec, clean) {
    var wrap = sec.querySelector('.pdp-homes__video');
    var stories = qa(sec, '.pdp-homes__story');
    if (!wrap || !stories.length) return;
    stories.forEach(function (b) {
      on(clean, b, 'click', function () {
        stories.forEach(function (x) { var onn = x === b; x.classList.toggle('is-on', onn); x.setAttribute('aria-pressed', onn ? 'true' : 'false'); });
        if (wrap.__pdpLoad) wrap.__pdpLoad(b.getAttribute('data-video'), b.getAttribute('data-poster'), b.getAttribute('data-desc'), b.getAttribute('data-quote'));
        var play = wrap.querySelector('[data-pdp-play]');
        if (play) play.click();
        if (MQ_M.matches) { var r = wrap.getBoundingClientRect(); if (r.top < 0 || r.bottom > window.innerHeight) smoothTo(wrap); }
      });
    });
  };

  S.faq = function (sec, clean) {
    var heads = qa(sec, '.pdp-faq__q button');
    heads.forEach(function (h) {
      on(clean, h, 'click', function () {
        var open = h.getAttribute('aria-expanded') !== 'true';
        h.setAttribute('aria-expanded', open ? 'true' : 'false');
        var item = h.closest('.pdp-faq__item'); if (item) item.classList.toggle('is-open', open);
      });
    });
  };

  S.final = function (sec, clean) {
    var btn = sec.querySelector('.pdp-final__cta');
    var status = sec.querySelector('[data-pdp-final-status]');
    var form = sec.querySelector('form');
    if (!btn || !form) return;
    var label = btn.querySelector('span') ? btn.querySelector('span').textContent : '';
    var pending = false;
    /* Dawn's own submit handler runs first (and sets aria-disabled), so only mark the attempt */
    on(clean, form, 'submit', function () {
      if (btn.disabled) return;
      pending = true; btn.setAttribute('data-state', 'loading');
      if (status) status.textContent = 'Adding Droplet to your cart…';
    });
    /* constants.js declares PUB_SUB_EVENTS with const, so it is a global binding, not a window property */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
      var offOk = subscribe(PUB_SUB_EVENTS.cartUpdate, function (d) {
        if (!pending || !d || d.source !== 'product-form') return;
        pending = false; btn.setAttribute('data-state', 'success');
        var s = btn.querySelector('span'); if (s) s.textContent = 'Added to Cart ✓';
        if (status) status.textContent = 'Droplet added to your cart.';
        window.setTimeout(function () { btn.removeAttribute('data-state'); if (s) s.textContent = label; }, 2200);
      });
      var offErr = subscribe(PUB_SUB_EVENTS.cartError, function () {
        if (!pending) return; pending = false; btn.removeAttribute('data-state');
        if (status) status.textContent = 'Something went wrong, please try again.';
      });
      clean.push(function () { try { offOk(); offErr(); } catch (e) {} });
    }
    var compat = sec.querySelector('.pdp-final__compat');
    if (compat) on(clean, compat, 'click', function (e) { var t = document.getElementById('pdp-fit'); if (t) { e.preventDefault(); smoothTo(t); } });
  };

  /* ------------------------------------------------------------ lifecycle */
  /* phone art direction for drawings that share a crop with a photograph:
     the mainline house is shown as a left-anchored 4:3 "cover" crop on phones,
     so its overlay drawing must slice the same way */
  function artDirect(scope) {
    var phone = MQ_M.matches && !STATIC.matches;
    qa(scope, '.pdp-main__svg').forEach(function (svg) {
      if (!svg.__pdpPAR) svg.__pdpPAR = svg.getAttribute('preserveAspectRatio') || 'xMidYMid meet';
      svg.setAttribute('preserveAspectRatio', phone ? 'xMinYMid slice' : svg.__pdpPAR);
    });
  }
  function boot(scope) {
    artDirect(scope || document);
    qa(scope || document, '[data-pdp-section]').forEach(function (sec) {
      if (sec.__pdpClean) return;
      var clean = [];
      sec.__pdpClean = clean;
      try { generic(sec, clean); } catch (e) { if (window.console) console.error('[droplet-pdp] generic', e); }
      var fn = S[sec.getAttribute('data-pdp-section')];
      if (fn) { try { fn(sec, clean); } catch (e) { if (window.console) console.error('[droplet-pdp] ' + sec.getAttribute('data-pdp-section'), e); } }
    });
    schedule();
  }
  function teardown(scope) {
    var list = qa(scope, '[data-pdp-section]');
    if (scope.matches && scope.matches('[data-pdp-section]')) list.push(scope);
    list.forEach(function (sec) {
      (sec.__pdpClean || []).forEach(function (f) { try { f(); } catch (e) {} });
      sec.__pdpClean = null;
    });
  }

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { teardown(e.target); });
  /* bfcache restore: re-measure everything */
  window.addEventListener('pageshow', function (e) { if (e.persisted) { tracks.forEach(function (t) { t.__pdpP = undefined; }); schedule(); } });
  /* a change of layout mode (rotation, crossing the phone breakpoint, turning
     reduced motion on or off) rebuilds every chapter from scratch, so video vs
     stills, pinned vs in-flow and all measurements start clean */
  var rebootT = 0;
  /* remember which chapter the reader is in (and how far through it), so a
     rotation lands them back in the same place rather than wherever the new
     page height happens to put the old scroll offset. Only positions read at
     the settled viewport size count: by the time the media query reports the
     change, the browser has already relaid (and possibly clamped) the page. */
  var place = { sec: null, frac: 0 }, stableW = window.innerWidth, stableH = window.innerHeight, placeRaf = 0, stableT = 0;
  var readPlace = function () {
    placeRaf = 0;
    if (rebootT || window.innerWidth !== stableW || Math.abs(window.innerHeight - stableH) > 160) return;
    var y = window.innerHeight * 0.4;
    qa(document, '[data-pdp-section]').some(function (sec) {
      var r = sec.getBoundingClientRect();
      if (r.top <= y && r.bottom > y) { place.sec = sec; place.frac = r.height ? clamp(-r.top / r.height, 0, 1) : 0; return true; }
      return false;
    });
  };
  window.addEventListener('scroll', function () { if (!placeRaf) placeRaf = window.requestAnimationFrame(readPlace); }, { passive: true });
  window.addEventListener('resize', function () {
    window.clearTimeout(stableT);
    stableT = window.setTimeout(function () { if (!rebootT) { stableW = window.innerWidth; stableH = window.innerHeight; } }, 400);
  });
  var onMode = function () {
    window.clearTimeout(rebootT);
    rebootT = window.setTimeout(function () {
      teardown(document.documentElement); boot(document); tracks.forEach(function (t) { t.__pdpP = undefined; });
      var a = place.sec;
      if (a && document.documentElement.contains(a)) {
        var r = a.getBoundingClientRect();
        window.scrollTo({ top: Math.round(window.pageYOffset + r.top + place.frac * r.height), behavior: 'instant' });
      }
      stableW = window.innerWidth; stableH = window.innerHeight;
      rebootT = 0;
      schedule();
    }, 120);
  };
  [RM, SHORT, MQ_M].forEach(function (m) { if (m.addEventListener) m.addEventListener('change', onMode); else if (m.addListener) m.addListener(onMode); });

  window.__dropletPDP = { boot: boot, teardown: teardown };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(document); }, { once: true });
  else boot(document);
})();
